<template>
  <div>
    <div class="row">
      <div class="col-sm-12 col-lg-5 form-group text-left">
        <label class="control-label"> {{ $t("common.unit") }} </label>
        <v-select
          v-model="localData.department"
          :options="departmentList"
          :reduce="(option) => option"
          :placeholder="$t('common.unit')"
          @input="onDepartmentSelected"
          label="name"
          autocomplete="off"
          :append-to-body="true"
          :class="{ 'field-error': $v.localData.department.$error }"
        ></v-select>
      </div>
    </div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-5 text-left">
        <label class="control-label">{{ $t("common.name") }}</label>
        <input
          type="text"
          v-model="localData.name"
          :placeholder="$t('common.name')"
          :class="{ 'field-error': $v.localData.name.$error }"
          class="form-control"
          @input="emitChange"
        />
      </div>
      <div class="form-group col-sm-12 col-lg-5 text-left">
        <label class="control-label">{{ $t("common.registration_number") }}</label>
        <input
          type="text"
          v-model="localData.registration_number"
          :class="{ 'field-error': $v.localData.registration_number.$error }"
          class="form-control"
          @input="emitChange"
        />
      </div>
    </div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-5 text-left">
        <label class="control-label">{{ $t("common.work_type") }}</label>
        <v-select
          v-model="localData.work_type"
          :options="work_list"
          :reduce="(option) => option"
          :placeholder="$t('works.work_machines.work_type_search')"
          @input="onWorkSelected"
          label="name"
          autocomplete="off"
          :append-to-body="true"
        ></v-select>
      </div>
      <div class="form-group col-sm-12 col-lg-5 text-left">
        <label class="control-label">{{ $t("remote_id") }}</label>
        <input type="text" v-model="localData.remote_id" @keydown="onlyNumbers" class="form-control" @input="emitChange" />
      </div>
    </div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-5 text-left">
        <label class="control-label">{{ $t("common.serial_number") }}</label>
        <input type="text" v-model="localData.series_number" class="form-control" @input="emitChange" />
      </div>
    </div>
    <div class="row checkbox-machine-options">
      <div class="col-sm-6 col-lg-3">
        <b-form-checkbox switch size="lg" v-model="autopropulsat" @change="onAutopropulsatChanged">{{
          $t("works.work_machines.autopropulsat")
        }}</b-form-checkbox>
      </div>
    </div>
    <div class="row checkbox-machine-options">
      <div class="col-sm-6 col-lg-3">
        <b-form-checkbox switch size="lg" v-model="equipment" @change="onEquipmentChanged">{{
          $t("works.work_machines.equipment")
        }}</b-form-checkbox>
      </div>
    </div>
    <div class="row checkbox-machine-options">
      <div class="col-sm-6 col-lg-3">
        <b-form-checkbox switch size="lg" v-model="is_dataller" @change="onIsDatallerChanged">{{
          $t("works.work_machines.dataller")
        }}</b-form-checkbox>
      </div>
    </div>
    <div class="row" style="margin-top: 20px;">
      <div class="form-group col-sm-12 col-lg-5 text-left">
        <label class="control-label mb-2">{{ $t("common.active") }}</label>
        <Toggle
          :is-active="isActive"
          :on-change="onIsActiveChanged"
          :has-toggle-text="false"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import required from "vuelidate/src/validators/required";
import { only_numbers } from "@/helpers/common";
import vSelect from "vue-select";
import Toggle from "@/components/Toggle.vue";

export default {
  name: "WorkMachineFormStep",
  components: {
    vSelect,
    Toggle,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      localData: { ...this.value },
      autopropulsat: Boolean(this.value.autopropulsat),
      equipment: Boolean(this.value.equipment),
      is_dataller: Boolean(this.value.is_dataller),
      work_list: [],
    };
  },
  validations() {
    return {
      localData: {
        department: { required },
        name: { required },
        registration_number: { required },
      },
    };
  },
  computed: {
    ...mapGetters({
      departments: "company/departments",
    }),
    isActive() {
      return this.localData?.is_active !== 0;
    },
    departmentList() {
      if (this.departments) {
        return this.departments.map((item) => ({
          value: item.uuid,
          name: item.name,
        }));
      }
      return [];
    },
  },
  methods: {
    ...mapActions({
      getWorks: "works-manager/getWorks",
      getDepartments: "company/getDepartments",
    }),
    onlyNumbers(event) {
      return only_numbers(event);
    },
    onAutopropulsatChanged(val) {
      this.localData.autopropulsat = val ? 1 : 0;
      this.emitChange();
    },
    onEquipmentChanged(val) {
      this.localData.equipment = val ? 1 : 0;
      this.emitChange();
    },
    onIsDatallerChanged(val) {
      this.localData.is_dataller = val ? 1 : 0;
      this.emitChange();
    },
    onIsActiveChanged(isActive) {
      this.localData.is_active = isActive ? 1 : 0;
      this.emitChange();
    },
    onDepartmentSelected(data) {
      this.localData.department_uuid = data?.value ?? null;
      this.localData.department = data ?? null;
      this.emitChange();
    },
    onWorkSelected(data) {
      this.localData.work_type = data ?? null;
      this.localData.work_uuid = data?.uuid ?? null;
      this.emitChange();
    },
    emitChange() {
      this.$emit("input", { ...this.localData });
    },
    validate() {
      this.$v.$touch();
      const errors = [];
      if (this.$v.localData.department.$invalid || this.$v.localData.name.$invalid || this.$v.localData.registration_number.$invalid) {
        errors.push(this.$t("fill_out_error"));
      }
      return errors;
    },
  },
  mounted() {
    this.getDepartments({ limit: 1000 });
    this.getWorks({
      limit: 1000,
    }).then((resp) => {
      this.work_list = resp && resp.data ? resp.data : [];
    });
  },
  watch: {
    value: {
      deep: true,
      handler(newVal) {
        this.localData = { ...newVal };
        this.autopropulsat = Boolean(newVal.autopropulsat);
        this.equipment = Boolean(newVal.equipment);
        this.is_dataller = Boolean(newVal.is_dataller);
      },
    },
  },
};
</script>

<style scoped>
.checkbox-machine-options {
  margin-top: 20px;
}
</style>
